import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import API_BASE_URL, { API_IMAGE_BASE_URL } from "../../api/config";
import axios from "axios";
import CertificateCanvas from "./CertificateCanvas";

const CertificateSection = () => {
  const [certificates, setCertificates] = useState([]);
  const [canvasCertificates, setCanvasCertificates] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentCertIndex, setCurrentCertIndex] = useState(0);

  const getCookie = (name) => {
    const cookieString = document.cookie;
    const cookies = cookieString.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.startsWith(name + "=")) {
        return cookie.substring(name.length + 1);
      }
    }
    return null; // Return null if cookie not found
  };

  useEffect(() => {
    const fetchProfileAndCertificates = async () => {
      try {
        const useremail = getCookie("email");
        if (!useremail) {
          console.error("No email found in cookies.");
          return;
        }

        const profileRes = await fetch(
          `${API_BASE_URL}/api/profile/read_byEmail?email=${useremail}`,
          {
            credentials: "include",
          },
        );

        let userId = null;
        if (profileRes.ok) {
          const rawData = await profileRes.json();
          const userData = rawData.data;
          userId = userData?.id;
        }

        let certData = [];
        if (userId) {
          // Step 2: Fetch certificates by user ID
          const certRes = await fetch(
            `${API_BASE_URL}/api/certificates/read_byUserId?user_id=${userId}`,
            {
              credentials: "include",
            },
          );
          if (certRes.ok) {
            const rawCertData = await certRes.json();
            certData = rawCertData.data;
          }
        }

        // Fetch event bookings for canvas certificates
        const getCookieExact = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(";").shift();
          return null;
        };

        const [eventsRes, bookingsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/upcoming_events/read`),
          axios
            .get(`${API_BASE_URL}/api/event_bookings/read_byEmail`, {
              params: { email: useremail },
              withCredentials: true,
            })
            .catch((e) => ({ data: [] })),
        ]);

        const allEvents = eventsRes.data?.data ?? eventsRes.data ?? [];
        const userBookings = bookingsRes.data?.data ?? bookingsRes.data ?? [];

        let approvedBookings = [];
        if (Array.isArray(userBookings) && Array.isArray(allEvents)) {
          const eventsMap = allEvents.reduce(
            (acc, ev) => ({ ...acc, [ev.id]: ev }),
            {},
          );
          approvedBookings = userBookings
            .map((booking) => {
              const details = eventsMap[booking.event_id];
              if (!details) return null;
              return {
                ...booking,
                event_name: details.name,
                event_date: details.date,
                certificate_approved: booking.certificate_approved || 0,
                user_name:
                  booking.user_name || booking.name || useremail.split("@")[0],
              };
            })
            .filter(
              (b) =>
                b &&
                (String(b.certificate_approved) === "1" ||
                  b.certificate_approved === 1),
            );
        }

        const visibleCertificates = Array.isArray(certData)
          ? certData
              .filter((cert) => cert.isShown === "1" || cert.isShown === 1)
              .map((cert) => {
                const raw = cert.certificate_path;
                const fixed =
                  raw?.replace("/home/ethicraf/public_html/", "") || "";
                return { ...cert, certificate_path: fixed };
              })
          : [];

        if (visibleCertificates.length === 0 && approvedBookings.length === 0) {
          Swal.fire({
            icon: "info",
            title: "No Certificates Issued",
            text: "No certificate has been issued to you till now.",
          });
        }

        setCertificates(visibleCertificates);
        setCanvasCertificates(approvedBookings);
      } catch (err) {
        console.error(
          "Error fetching profile, certificates, or bookings:",
          err,
        );
        setCertificates([]);
        setCanvasCertificates([]);
      }
    };

    fetchProfileAndCertificates();
  }, []);

  const openFullscreen = (index) => {
    setCurrentCertIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const nextCertificate = useCallback(() => {
    setCurrentCertIndex((prevIndex) => (prevIndex + 1) % certificates.length);
  }, [certificates.length]);

  const prevCertificate = useCallback(() => {
    setCurrentCertIndex(
      (prevIndex) =>
        (prevIndex - 1 + certificates.length) % certificates.length,
    );
  }, [certificates.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isFullscreen) return;

      if (event.key === "ArrowRight") nextCertificate();
      if (event.key === "ArrowLeft") prevCertificate();
      if (event.key === "Escape") closeFullscreen();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen, nextCertificate, prevCertificate, closeFullscreen]);

  return (
    <div className="certificate-container">
      <main className="main-certificate-content">
        {certificates.length === 0 && canvasCertificates.length === 0 ? (
          <h2 className="no-certificates-text">
            No certificate issued to you till yet.
          </h2>
        ) : (
          <section
            className="certificate-grid"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "20px",
              justifyContent: "center",
            }}
          >
            {certificates.map((cert, index) => (
              <div
                key={cert.id}
                className="certificate-card"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => openFullscreen(index)}
              >
                <div className="card-image-container">
                  <img
                    src={`${API_IMAGE_BASE_URL}/${cert.certificate_path}`}
                    alt="Certificate"
                    className="certificate-thumbnail"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://placehold.co/600x400/1e1e2d/ff6347?text=Image+Error";
                    }}
                  />
                </div>
              </div>
            ))}

            {/* Render dynamically generated canvas certificates */}
            {canvasCertificates.map((certData, index) => (
              <div
                key={`canvas-${certData.booking_id || index}`}
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <CertificateCanvas
                  data={{
                    eventName: certData.event_name,
                    eventDate: certData.event_date || new Date().toISOString(),
                    userName: certData.user_name || "Participant",
                  }}
                />
              </div>
            ))}
          </section>
        )}
      </main>

      {isFullscreen && (
        <div className="fullscreen-modal-overlay" onClick={closeFullscreen}>
          <div
            className="fullscreen-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${API_IMAGE_BASE_URL}/${certificates[currentCertIndex]?.certificate_path}`}
              alt="Certificate"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://placehold.co/1200x800/1e1e2d/ff6347?text=Display+Error";
              }}
            />
            <button className="modal-nav-button left" onClick={prevCertificate}>
              &larr;
            </button>
            <button
              className="modal-nav-button right"
              onClick={nextCertificate}
            >
              &rarr;
            </button>
            <button className="modal-close-button" onClick={closeFullscreen}>
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateSection;
